import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';

const PYLON_APP_ID = process.env.REACT_APP_PYLON_APP_ID;

/**
 * Removes the Pylon widget from the page so it can be re-initialized
 * with fresh chat_settings (e.g. after an org switch).
 *
 * Pylon's JS API has no "identify" / "update" method — the widget reads
 * window.pylon.chat_settings once on load and caches the identity.
 * The only reliable way to change the identified account is to tear
 * everything down and start over.
 */
function destroyPylonWidget() {
  document
    .querySelectorAll('script[src*="widget.usepylon.com"]')
    .forEach((el) => el.remove());

  const chat = document.getElementById('pylon-chat');
  if (chat) chat.remove();

  delete window.pylon;
  delete window.Pylon;
}

/**
 * Initializes the Pylon Chat Widget with the current user's info.
 *
 * Each Boon user gets a *separate* Pylon Contact per org using +alias
 * emails (e.g. user+orgid@domain.com). This is the Pylon-recommended
 * approach for contacts that belong to multiple accounts.
 *
 * The alias email and its HMAC hash are pre-computed server-side and
 * returned in the user API response as `pylon_org_identities`.
 *
 * When the user switches orgs the widget is destroyed and re-created
 * with the new org's alias email, hash, and account_external_id.
 *
 * Users without an org are linked to the "Boon View" default account
 * (external_id=0, domain=goboon.co) so their issues appear under a
 * known account in Pylon rather than being unlinked.
 *
 * Should be called once in a top-level authenticated component (e.g. DashboardBase).
 */
const usePylonChat = () => {
  const currentUser = useSelector((state) => state.authReducer.currentUser?.user);
  const currentOrg = useSelector((state) => state.organizationsReducer.currentOrganization);
  const scriptLoadedRef = useRef(false);
  const prevOrgIdRef = useRef(undefined);

  // Resolve the Boon org ID and Pylon account ID from whichever shape Redux gives us
  const orgId = currentOrg?.organization?.id || currentOrg?.id || null;
  const orgIdStr = orgId ? String(orgId) : null;
  const pylonAccountId =
    currentOrg?.organization?.pylon_account_id || currentOrg?.pylon_account_id || null;

  // "Boon View" default account — used when user has no org
  const boonViewAccountId = currentUser?.pylon_boon_view_account_id || null;

  // Final account values: org-specific or "Boon View" fallback
  const effectiveAccountId = pylonAccountId || boonViewAccountId;
  const effectiveExternalId = orgIdStr || '0'; // 0 = Boon View external_id

  // Look up the per-org Pylon identity (alias email + HMAC hash)
  const pylonIdentity = orgIdStr
    ? currentUser?.pylon_org_identities?.[orgIdStr]
    : null;

  // Tear down + re-init when the active org changes
  useEffect(() => {
    if (prevOrgIdRef.current !== undefined && orgIdStr && prevOrgIdRef.current !== orgIdStr) {
      destroyPylonWidget();
      scriptLoadedRef.current = false;
    }
    prevOrgIdRef.current = orgIdStr;
  }, [orgIdStr]);

  // Load the Pylon widget script (runs once, or again after a destroy)
  useEffect(() => {
    if (!PYLON_APP_ID || scriptLoadedRef.current) return;

    if (!window.Pylon) {
      const pylon = function (...args) {
        pylon.e(...args);
      };
      pylon.q = [];
      pylon.e = function (e) {
        pylon.q.push(e);
      };
      window.Pylon = pylon;
    }

    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.async = true;
    script.src = `https://widget.usepylon.com/widget/${PYLON_APP_ID}`;
    document.head.appendChild(script);
    scriptLoadedRef.current = true;
  }); // no deps — re-evaluated every render to pick up the flag reset

  // Set chat_settings whenever user or org changes
  useEffect(() => {
    if (!PYLON_APP_ID || !currentUser) return;

    const userName =
      [currentUser.first_name, currentUser.last_name].filter(Boolean).join(' ') || 'Unknown User';

    // Use the per-org alias email + hash; fall back to real email if not available yet
    const email = pylonIdentity?.email || currentUser.email;
    const emailHash = pylonIdentity?.email_hash || currentUser.pylon_email_hash;

    window.pylon = {
      chat_settings: {
        app_id: PYLON_APP_ID,
        email,
        name: userName,
        ...(emailHash && { email_hash: emailHash }),
        ...(effectiveAccountId && { account_id: effectiveAccountId }),
        ...(effectiveExternalId && { account_external_id: effectiveExternalId }),
      },
    };
  }, [currentUser, pylonIdentity, effectiveAccountId, effectiveExternalId]);
};

export default usePylonChat;
